import React, {useEffect, useReducer} from 'react';
import {Badge, Button, Col, Form, Modal, Row} from '@themesberg/react-bootstrap';
import {faCircleInfo, faDesktop, faEye, faGlobe, faMobileScreen, faPen, faPowerOff, faTrashCan} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {FormattedMessage} from 'react-intl';
import Utils from '../services/Utils';
import {useHistory} from 'react-router-dom';
import API from '../services/API';
import PageHeader from '../components/PageHeader';
import CrudTable from '../components/CrudTable';
import TranslationsField, {LanguageChip, NameWithTranslations, countTranslations} from '../components/TranslationsField';
import {usePlan} from '../services/PlanContext';

export default (props) => {

  const formReducer = (state, event) => {
    if (event.value !== undefined) {
      return {
        ...state,
        [event.name]: event.value
      }
    } else {
      return event;
    }
  }

  const {intl} = props;
  const {plan, resourceLimit, openSubscription} = usePlan();
  const history = useHistory();
  const [showModal, setShowModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [editModalMode, setEditModalMode] = React.useState(false);
  const [currentFieldId, setCurrentFieldId] = React.useState(false);
  const [currentFieldName, setCurrentFieldName] = React.useState('');
  const [fields, setFields] = React.useState([]);
  // Limite calculée sur les champs NON par défaut de la liste locale (le backend ne compte pas les
  // champs par défaut), à jour après création/suppression, pas sur le compteur du contexte plan.
  const locked = plan === 'FREE' && resourceLimit != null && fields.filter(f => !f.defaultField).length >= resourceLimit;
  const [formData, setFormData] = useReducer(formReducer, {});
  const [showPreview, setShowPreview] = React.useState(false);
  const [previewDevice, setPreviewDevice] = React.useState('mobile');
  // Langue des libellés principaux (langue par défaut du module) : les autres langues sont saisies dans le bloc
  // Traductions, proposé uniquement pour les champs personnalisés (un champ natif est déjà traduit par l'appli).
  const [defaultLanguage, setDefaultLanguage] = React.useState('fr');
  const [translationsOpen, setTranslationsOpen] = React.useState(false);
  const [modalOpenCount, setModalOpenCount] = React.useState(0);

  const refreshFields = () => {
    API.getBookingFields(history).then(response => {
      if (response && response.data.success === true) {
        const fields = response.data.data.fields.sort(function (a, b) {
          return a.position - b.position
        });
        setFields(fields);
        if (response.data.data.defaultLanguage) {
          setDefaultLanguage(response.data.data.defaultLanguage);
        }
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }
    refreshFields();
  }, []);

  const handleAddOrEdit = (e) => {
    e.preventDefault();
    if (editModalMode) {
      API.putFields(history, formData, currentFieldId).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'SAVED_CHANGES'}));
          setShowModal(false);
          refreshFields();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    } else {
      API.postBookingFields(history, formData).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'FIELD_ADDED'}));
          setShowModal(false);
          refreshFields();
        } else if (response && response.data && response.data.data === 'PLAN_LIMIT_REACHED') {
          setShowModal(false);
          openSubscription('RESOURCE_LIMIT');
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    }
  }

  const handleChange = event => {
    const isCheckbox = event.target.type === 'checkbox';
    setFormData({
      name: event.target.id,
      value: isCheckbox ? event.target.checked : event.target.value
    })
  }

  const handleChangeTranslations = (translations) => {
    setFormData({
      name: 'translations',
      value: translations
    });
  }

  const handleNewFieldButton = () => {
    setFormData({});
    setTranslationsOpen(false);
    setModalOpenCount(c => c + 1);
    setEditModalMode(false);
    setShowModal(true);
  }

  const handleEditFieldButton = (props) => {
    setFormData(props);
    setTranslationsOpen(countTranslations(props.translations) > 0);
    setModalOpenCount(c => c + 1);
    setCurrentFieldId(props.id);
    setEditModalMode(true);
    setShowModal(true);
  }

  const handleEnableOrDisableButton = (props) => {
    if (props.active) {
      API.getDisableField(history, props.id).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'FIELD_DISABLED'}));
          refreshFields();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    } else {
      API.getEnableField(history, props.id).then(response => {
        if (response && response.data.success === true) {
          // Gratuit : activer un 2ᵉ champ perso bascule l'actif au lieu de bloquer -> toast dédié.
          const switched = response.data.data === 'RESOURCE_SWITCHED';
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: switched ? 'FIELD_SWITCHED' : 'FIELD_ENABLED'}));
          refreshFields();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    }
  }

  const handleDeleteFieldButton = (props) => {
    setCurrentFieldId(props.id);
    setCurrentFieldName(props.name1 + (props.name2 ? ' - ' + props.name2 : ''));
    setShowDeleteModal(true);
  }

  // a little function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    // update position variable
    let index = 0;
    for (const [key, value] of Object.entries(result)) {
      value.position = index++;
    }

    return result;
  };

  const handleDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      fields,
      result.source.index,
      result.destination.index
    );

    setFields(items);

    // Prepare data for backend
    let data = {
      fields: []
    };

    for (const [key, value] of Object.entries(items)) {
      data.fields.push({
        id: value.id,
        position: value.position
      });
    }

    API.postReorderFields(history, data).then(response => {
      if (response && response.data.success === true) {
        // Everything is OK
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  const deleteField = () => {
    API.deleteFields(history, currentFieldId).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'FIELD_DELETED'}));
        setShowDeleteModal(false);
        refreshFields();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  // Valeur d'une colonne de nom : libellé traduit pour les champs par défaut,
  // saisi pour les champs personnalisés, suivi du badge « Obligatoire » éventuel.
  const fieldName = (f, n) => {
    const raw = n === 1 ? f.name1 : f.name2;
    const required = n === 1 ? f.required1 : f.required2;
    const text = (f.defaultField && raw) ? intl.formatMessage({id: raw}) : raw;
    // Pastille « n langues » sur la colonne 1 des champs personnalisés traduits
    const label = (!f.defaultField && n === 1) ? <NameWithTranslations name={text} translations={f.translations}/> : text;
    return (
      <>
        {label}
        {required ? <> <Badge bg="primary" className="ms-1"><FormattedMessage id="REQUIRED"/></Badge></> : null}
      </>
    );
  };

  return (
    <div className="ccv2-crud">
      <PageHeader title="BOOKING_PAGE" description="BOOKING_PAGE_DESC"/>

      <CrudTable
        draggable
        getRowId={(f) => String(f.position)}
        onDragEnd={handleDragEnd}
        items={fields}
        intl={intl}
        columns={[
          {key: 'name1', header: `${intl.formatMessage({id: 'COLUMN'})} 1`, priority: 1, render: (f) => fieldName(f, 1)},
          {key: 'name2', header: `${intl.formatMessage({id: 'COLUMN'})} 2`, priority: 2, render: (f) => fieldName(f, 2), mobileLabelOnly: true, mobileHidden: (f) => !f.name2},
          {key: 'active', header: 'ACTIVE_TAB', type: 'status', priority: 2},
        ]}
        actions={[
          {key: 'edit', icon: faPen, label: 'EDIT', onClick: handleEditFieldButton, hidden: (f) => f.defaultField},
          {key: 'toggle', icon: faPowerOff, label: (f) => (f.active ? 'DISABLE' : 'ENABLE'), intent: 'warn', onClick: handleEnableOrDisableButton},
          {key: 'delete', icon: faTrashCan, label: 'DELETE', intent: 'danger', onClick: handleDeleteFieldButton, hidden: (f) => f.defaultField},
        ]}
        toolbar={{
          onNew: handleNewFieldButton,
          newLabel: 'NEW_FIELD',
          locked,
          extraActions: [
            {key: 'preview', icon: faEye, label: 'PREVIEW', variant: 'secondary', onClick: () => setShowPreview(true)},
          ],
        }}
        empty={{icon: faGlobe, title: 'EMPTY_FIELDS_TITLE', description: 'EMPTY_FIELDS_DESC'}}
      />

      <Modal as={Modal.Dialog} centered show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6">{editModalMode ? <FormattedMessage id="EDIT_FIELD"/> : <FormattedMessage id="NEW_FIELD"/>}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowModal(false)}/>
        </Modal.Header>
        <Modal.Body>
          <Form id="add-field-form" onSubmit={handleAddOrEdit}>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="cc-label-with-lang">
                    <span><FormattedMessage id="FIELD_NAME"/> 1</span>
                    {!formData.defaultField && <LanguageChip code={defaultLanguage}/>}
                  </Form.Label>
                  <Form.Control required type="text" id="name1" maxLength="255"
                                onChange={handleChange} value={formData.name1 || ''}/>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label></Form.Label>
                <br/><br/>
                <Form.Check label={intl.formatMessage({id: 'REQUIRED_FIELD'})} id="required1"
                            onChange={handleChange}
                            checked={formData.required1 || false}/>
              </Col>
            </Row>
            <br/>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="FIELD_NAME"/> 2</Form.Label>
                  <Form.Control type="text" id="name2" maxLength="255"
                                onChange={handleChange} value={formData.name2}/>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label></Form.Label>
                <br/><br/>
                <Form.Check label={intl.formatMessage({id: 'REQUIRED_FIELD'})} id="required2"
                            onChange={handleChange}
                            checked={formData.required2 || false}/>
              </Col>
            </Row>
            {!formData.defaultField &&
              <TranslationsField key={modalOpenCount} idPrefix="field-translation"
                                 value={formData.translations || {}}
                                 onChange={handleChangeTranslations}
                                 defaultLanguage={defaultLanguage}
                                 fields={[{key: 'name1', baseName: formData.name1 || ''}, {key: 'name2', baseName: formData.name2 || ''}]}
                                 defaultOpen={translationsOpen}/>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" type="submit" form="add-field-form">
            {editModalMode ? <FormattedMessage id="RECORD"/> : <FormattedMessage id="ADD"/>}
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal as={Modal.Dialog} centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="DELETE_FIELD" values={{name: currentFieldName}}/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowDeleteModal(false)}/>
        </Modal.Header>
        <Modal.Footer>
          <Button variant="danger" onClick={deleteField}>
            <FormattedMessage id="DELETE"/>
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowDeleteModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal size="xl" centered className="ccv2-preview-modal" dialogClassName="ccv2-preview-dialog" show={showPreview} onHide={() => setShowPreview(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="PREVIEW"/></Modal.Title>
          <div className="ccv2-preview-segmented" role="tablist">
            <button type="button" role="tab" aria-selected={previewDevice === 'desktop'}
                    className={`ccv2-preview-seg ${previewDevice === 'desktop' ? 'is-active' : ''}`}
                    onClick={() => setPreviewDevice('desktop')}>
              <FontAwesomeIcon icon={faDesktop}/><FormattedMessage id="DESKTOP"/>
            </button>
            <button type="button" role="tab" aria-selected={previewDevice === 'mobile'}
                    className={`ccv2-preview-seg ${previewDevice === 'mobile' ? 'is-active' : ''}`}
                    onClick={() => setPreviewDevice('mobile')}>
              <FontAwesomeIcon icon={faMobileScreen}/><FormattedMessage id="MOBILE"/>
            </button>
          </div>
          <Button variant="close" aria-label="Close" onClick={() => setShowPreview(false)}/>
        </Modal.Header>
        <Modal.Body>
          <div className="ccv2-preview-stage">
            <div className={`ccv2-preview-frame is-${previewDevice}`}>
              <iframe title={intl.formatMessage({id: 'PREVIEW'})} src={`/#/booking?id=preview&lang=${intl.locale}`}/>
            </div>
          </div>
          <p className="ccv2-preview-hint">
            <FontAwesomeIcon icon={faCircleInfo}/>
            <FormattedMessage id="PREVIEW_HINT"/>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="ccv2-btn ccv2-btn-secondary"
                  onClick={() => window.open(`/#/booking?id=preview&lang=${intl.locale}`, '_blank')}>
            <FormattedMessage id="OPEN_IN_NEW_TAB"/>
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowPreview(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
